import { useEffect, useRef } from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { photoUrlFromKey } from '@leanin/shared/media-url';
import { trackGroupViewed } from '@/lib/analytics/events';
import { useMember } from '@/lib/use-session';
import { SpaceDetailView } from '@/components/spaces/SpaceDetailView';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import { spacesKeys } from '@/lib/queries/spaces';
import { useInfiniteGroupMembers } from '@/lib/queries/groups';
import { useInfiniteFeed } from '@/lib/queries/feed';

export const Route = createFileRoute('/_authed/groups/$id/')({
  component: SpaceDetailPage,
});

// The route owns every query the detail page needs and hands each slice to
// `SpaceDetailView` as `T | null` (or `undefined` while pending). The view
// renders its skeleton chrome + per-region fallbacks until each slice
// resolves — there is no parallel skeleton tree.
function SpaceDetailPage() {
  const { id } = Route.useParams();
  const me = useMember();

  const spaceQuery = useQuery({
    ...spacesKeys.detail(id),
    retry: false,
  });

  // Roster is members-only server-side (403 otherwise): fetch only when the
  // viewer actually joined or is a leader/admin. Cursor-paginated so the
  // roster grows past the first page via the "Load more members" button.
  const rosterEnabled =
    spaceQuery.data != null &&
    (spaceQuery.data.viewerHasJoined || spaceQuery.data.viewerRole === 'leader');
  const membersQuery = useInfiniteGroupMembers(id, {
    pageSize: 50,
    enabled: rosterEnabled,
  });

  const relatedQuery = useQuery({
    ...spacesKeys.related(id),
    enabled: spaceQuery.data != null,
  });
  // Infinite feed so the space's posts grow past the first page via the
  // "Load more" button (mirrors the canonical roster affordance). Keyed under
  // feedKeys.list so post mutations' feedKeys.lists() invalidation reaches it.
  const postsQuery = useInfiniteFeed({ scope: `group:${id}` });

  const viewedSpaceIdRef = useRef<string | null>(null);
  useEffect(() => {
    const detail = spaceQuery.data;
    if (detail == null || viewedSpaceIdRef.current === detail.id) return;
    viewedSpaceIdRef.current = detail.id;
    trackGroupViewed({
      group_type: 'group',
      is_member: detail.viewerHasJoined,
      is_leader: detail.viewerRole === 'leader',
    });
  }, [spaceQuery.data]);

  if (me === null) return null;
  // 404 → branded NotFoundPage directly (render-context `throw notFound()`
  // lands in TanStack's catch-boundary, not its 404 surface).
  if (
    isNotFoundApiError(spaceQuery.error) ||
    (spaceQuery.data === null && !spaceQuery.isLoading)
  ) {
    return <NotFoundPage />;
  }
  if (spaceQuery.isError) throw notFound();

  const space = spaceQuery.data ?? null;
  const members = membersQuery.data?.pages.flatMap((p) => p.items) ?? null;
  const related = relatedQuery.data ?? null;
  const posts = postsQuery.data?.pages.flatMap((p) => p.items) ?? null;

  const viewer = {
    id: me.id,
    firstName: me.firstName,
    lastName: me.lastName,
    photoUrl: photoUrlFromKey(me.photoStorageKey, me.photoStatus) ?? undefined,
  };

  return (
    <SpaceDetailView
      space={space}
      viewer={viewer}
      members={members}
      related={related}
      posts={posts}
      postsHasMore={postsQuery.hasNextPage}
      isPostsLoadingMore={postsQuery.isFetchingNextPage}
      onPostsLoadMore={() => void postsQuery.fetchNextPage()}
      membersHasMore={membersQuery.hasNextPage}
      isMembersLoadingMore={membersQuery.isFetchingNextPage}
      onMembersLoadMore={() => void membersQuery.fetchNextPage()}
    />
  );
}
